/**
 * ModuleControlPanel - Quick action buttons and deadline button
 * Standard kontrollpanel for alle module pages
 * @version 2.0.0 - MASTERPROMPT v2.0 compliant
 */
import { Link, useLocation } from 'react-router-dom';
import { MessageSquare, FileText, BookOpen, Calendar, Upload } from 'lucide-react';
import { useMemo, useEffect, useState } from 'react';
import { DocumentUploadModal } from '../DocumentUploadModal';

const controlBtnBase = `
  flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
  transition-all duration-200 ease-out
  bg-[var(--bg-surface)] text-[var(--text-muted)] 
  hover:text-[var(--text-main)] hover:bg-[var(--bg-highlight)] 
  border border-[var(--border-subtle)] hover:border-[var(--copper)]/40
`;

export function ModuleControlPanel({
  moduleId,
  guideRoute,
  activeDeadlines = 0,
  isFristerActive = false,
  onFristerClick,
  showDocuments = true,
  showChat = true,
  showGuide = true,
  showFrister = true,
  showUpload = true
}) {
  const location = useLocation();
  const [uploadOpen, setUploadOpen] = useState(false);
  
  // Sjekk om vi er i B2B-kontekst
  const isCompanyContext = useMemo(() => {
    const fromState = location.state?.fromCompany;
    const isCompanyPath = location.pathname.startsWith('/company/');
    return fromState || isCompanyPath || sessionStorage.getItem('rettwerk_company_context') === 'true';
  }, [location.pathname, location.state]);
  
  // Lagre kontekst i sessionStorage for guide-sidene
  useEffect(() => {
    if (isCompanyContext) {
      sessionStorage.setItem('rettwerk_company_context', 'true');
    }
  }, [isCompanyContext]);
  
  // Skjul guide-knappen for arbeidsrett i B2B-kontekst (den har egen dedikert guide)
  const shouldShowGuide = useMemo(() => {
    if (!showGuide || !guideRoute) return false;
    // Skjul guide-knappen for arbeidsrett-modulen i B2B-kontekst
    if (isCompanyContext && moduleId === 'arbeidsrett') return false;
    return true;
  }, [showGuide, guideRoute, isCompanyContext, moduleId]);
  
  // Dynamisk guide-rute basert på kontekst
  const dynamicGuideRoute = useMemo(() => {
    if (!guideRoute) return null;
    return guideRoute;
  }, [guideRoute]);
  
  // Dynamiske "Dokumenter" og "Chat" ruter for B2B-kontekst
  const documentsRoute = isCompanyContext ? '/company/documents' : '/documents';
  const chatRoute = isCompanyContext ? '/company/dashboard' : '/chat';

  // B2C: Åpne AI-chatten direkte via custom event
  const handleOpenAIChat = () => {
    if (!isCompanyContext) {
      window.dispatchEvent(new CustomEvent('open-ai-chat', { detail: { moduleId } }));
    }
  };
  
  return (
    <div className="flex items-center justify-between gap-4 mb-8">
      {/* Left-aligned: Quick Actions */}
      <div className="flex items-center gap-3">
        {showChat && (
          isCompanyContext ? (
            <Link
              to={chatRoute}
              className={controlBtnBase}
              data-testid="quick-action-chat"
            >
              <MessageSquare className="w-4 h-4 opacity-70" />
              <span>Tilbake til dashboard</span>
            </Link>
          ) : (
            <button
              type="button"
              onClick={handleOpenAIChat}
              className={controlBtnBase}
              data-testid="quick-action-chat"
            >
              <MessageSquare className="w-4 h-4 opacity-70" />
              <span>Spør juridisk assistent</span>
            </button>
          )
        )}
        {showDocuments && (
          <Link
            to={documentsRoute}
            className={controlBtnBase}
            data-testid="quick-action-documents"
          >
            <FileText className="w-4 h-4 opacity-70" />
            <span>Mine dokumenter</span>
          </Link>
        )}
        {showUpload && !isCompanyContext && (
          <button
            type="button"
            onClick={() => setUploadOpen(true)}
            className={`${controlBtnBase} !bg-[var(--copper)]/10 !border-[var(--copper)]/30 !text-[var(--copper)] hover:!bg-[var(--copper)]/20`}
            data-testid="quick-action-upload"
          >
            <Upload className="w-4 h-4" />
            <span>Last opp dokument</span>
          </button>
        )}
        {shouldShowGuide && dynamicGuideRoute && (
          <Link
            to={dynamicGuideRoute}
            target="_blank"
            className={controlBtnBase}
            data-testid="quick-action-guide"
          >
            <BookOpen className="w-4 h-4 opacity-70" />
            <span>Brukerveiledning</span>
          </Link>
        )}
      </div>
      
      {/* Right-aligned: Frister button */}
      {showFrister && onFristerClick && (
        <button
          onClick={onFristerClick}
          data-testid={`${moduleId}-frister-btn`}
          className={`
            relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
            transition-all duration-200 ease-out
            ${isFristerActive 
              ? 'bg-[var(--copper)] text-white shadow-lg shadow-[var(--copper)]/25' 
              : 'bg-[var(--bg-surface)] text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-highlight)] border border-[var(--border-subtle)] hover:border-[var(--copper)]/40'
            }
          `}
        >
          <Calendar className={`w-4 h-4 ${isFristerActive ? 'opacity-100' : 'opacity-70'}`} />
          <span>Frister</span>
          {activeDeadlines > 0 && !isFristerActive && (
            <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-[var(--copper)] text-white rounded-full shadow-md animate-pulse">
              {activeDeadlines}
            </span>
          )}
        </button>
      )}
      
      {/* Document Upload Modal */}
      <DocumentUploadModal
        moduleId={moduleId}
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
      />
    </div>
  );
}

export default ModuleControlPanel;
